"use client";

import React, { useEffect, useState } from "react";
import { motion, Variants } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { GoChevronRight } from "react-icons/go";
import { FacebookIcon, InstagramIcon, LinkedinIcon } from "lucide-react";
import ReservationForm from "@/components/menu/reservation-form/ReservationForm";
import { Blog } from "./page";

export default function BlogDetails({
  blog,
  relatedBlogs,
}: {
  blog: Blog;
  relatedBlogs: Blog[];
}) {
  const [activeSection, setActiveSection] = useState<string>("");
  const [readProgress, setReadProgress] = useState(0);

  // Track scroll progress and active section
  useEffect(() => {
    const handleScroll = () => {
      if (!blog) return;

      // Calculate read progress
      const mainContent = document.querySelector("main");
      if (mainContent) {
        const rect = mainContent.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const contentHeight = mainContent.offsetHeight;

        // Calculate how much of the content has been scrolled
        const scrolled = Math.max(0, -rect.top);
        const total = contentHeight - viewportHeight;
        const progress = Math.min(100, Math.max(0, (scrolled / total) * 100));

        setReadProgress(progress);
      }

      // Find active section
      const sections = blog.topics.map((topic) =>
        document.getElementById(topic.title.replace(/\s+/g, "-").toLowerCase())
      );

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section) {
          const rect = section.getBoundingClientRect();
          if (rect.top <= 200) {
            setActiveSection(
              blog.topics[i].title.replace(/\s+/g, "-").toLowerCase()
            );
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial call

    return () => window.removeEventListener("scroll", handleScroll);
  }, [blog]);

  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  if (!blog) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen space-y-4">
        <h1 className="text-3xl font-bold mb-4">Blog Not Found</h1>
        <p className="text-gray-600 mb-4">
          The blog you're looking for doesn't exist.
        </p>
      </div>
    );
  }

  return (
    <section>
      <div className="relative rounded-b-4xl overflow-hidden max-w-11/12 mx-auto">
        {/* Background Image */}
        <Image
          width={1440}
          height={732}
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
          src={blog.image}
          alt={blog.title}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-[#000000]/45 pointer-events-none"></div>

        {/* Header Content */}
        <motion.div
          className="relative z-10 p-5 lg:p-10 space-y-5 flex flex-col items-center justify-center h-full min-h-[500px]"
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.2,
              },
            },
          }}
        >
          <motion.div
            variants={fadeUp}
            className="flex justify-center items-center text-center bg-[000000]/25 border-none rounded-full px-4 py-3 mt-10"
          >
            <p className="text-sm font-light flex justify-center items-center gap-1 text-[#FFFFFF]/50">
              <Link href="/" className="hover:underline transition">
                Home
              </Link>
              <GoChevronRight />
              <Link href="/blogs" className="hover:underline transition">
                Blogs
              </Link>
              <GoChevronRight />
            </p>
            <p className="text-sm font-medium text-white md:pl-1">
              Blog Details
            </p>
          </motion.div>

          {/* Title & meta */}
          <motion.div
            variants={fadeUp}
            className="text-center text-white space-y-3"
          >
            <h1 className="font-semibold text-3xl md:text-5xl">{blog.title}</h1>

            <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-50 justify-center max-w-5xl mx-auto mt-10">
              {/* <div className="flex flex-col text-center">
                                <h2 className="text-[#FFFFFF]/[75%] font-semibold text-sm">Author</h2>
                                <p className="font-semibold text-[#FFFFFF]">{blog.author}</p>
                            </div> */}

              <div className="flex flex-col text-center">
                <h2 className="text-[#FFFFFF]/[75%] font-semibold text-sm">
                  Publish Date
                </h2>
                <p className="font-semibold text-[#FFFFFF]">{blog.date}</p>
              </div>

              <div className="flex flex-col text-center">
                <h2 className="text-[#FFFFFF]/[75%] font-semibold text-sm">
                  Category
                </h2>
                <p className="font-semibold text-[#FFFFFF]">{blog.category}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Blog Content */}
      <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="flex flex-col md:flex-row md:gap-8">
          {/* Left Sidebar */}
          <aside className="md:w-1/4 text-sm text-[#BCBCBC] space-y-2 mb-6 md:mb-0 text-center md:text-left md:sticky md:top-36 md:self-start">
            <p className="font-bold text-[#BCBCBC] text-base mb-4">Contents</p>

            {/* Progress Bar */}
            <div className="relative w-full h-1 bg-[#2A2A2A] rounded-full mb-4">
              <div
                className="absolute top-0 left-0 h-full bg-[#D4AF37] rounded-full transition-all duration-300"
                style={{ width: `${readProgress}%` }}
              ></div>
            </div>

            {blog.topics.map((topic, idx) => {
              const topicId = topic.title.replace(/\s+/g, "-").toLowerCase();
              const isActive = activeSection === topicId;

              return (
                <a
                  key={idx}
                  href={`#${topicId}`}
                  className={`block text-sm font-semibold transition-colors py-1 ${
                    isActive ? "text-[#D4AF37]" : "hover:text-[#D4AF37]"
                  }`}
                >
                  {topic.title}
                </a>
              );
            })}
          </aside>

          {/* Main Content */}
          <main className="md:w-3/4 space-y-10">
            {/* Excerpt */}
            {blog.excerpt && (
              <div className="text-lg text-[#BCBCBC] italic border-l-4 border-[#D4AF37] pl-4 mb-8">
                {blog.excerpt}
              </div>
            )}

            {/* Topics */}
            {blog.topics.map((topic, idx) => (
              <section
                key={idx}
                id={topic.title.replace(/\s+/g, "-").toLowerCase()}
                className="scroll-mt-36"
              >
                <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-[#FFFFFF]">
                  {topic.title}
                </h2>
                <p className="text-[#EEEEEE] leading-relaxed text-base md:text-lg">
                  {topic.description}
                </p>
                {topic?.src && (
                  <img
                    src={topic?.src as unknown as string}
                    alt={topic.title}
                    className="w-full h-auto rounded mt-5 mb-10 hover:scale-105 transition duration-500"
                  />
                )}
              </section>
            ))}
          </main>

          {/* Right Sidebar */}
          <aside className="md:w-1/4 text-sm text-[#FFFFFF] space-y-2 mb-6 md:mb-0 md:sticky md:top-36 pt-5 md:pt-0 md:self-start">
            <p className="font-bold text-center text-lg mb-4">Share</p>
            <div className="flex justify-center gap-6 pt-3">
              <FacebookIcon className="hover:scale-105 transition duration-200 hover:text-[#D4AF37] cursor-pointer" />
              <InstagramIcon className="hover:scale-105 transition duration-200 hover:text-[#D4AF37] cursor-pointer" />
              <LinkedinIcon className="hover:scale-105 transition duration-200 hover:text-[#D4AF37] cursor-pointer" />
            </div>
          </aside>
        </div>
      </div>

      {/* Similar Articles */}
      {relatedBlogs.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 border-t-2">
          <h2 className="text-3xl md:text-4xl font-semibold text-center text-white mb-12">
            Similar Articles
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedBlogs.map((relatedBlog) => (
              <Link
                key={relatedBlog.id}
                href={`/blogs/${relatedBlog.id}`}
                className="group bg-[#FAF6DB]/15 rounded-[40px] overflow-hidden border border-[#2A2A2A] hover:border-[#D4AF37] transition-all duration-300 h-full flex flex-col"
              >
                <div>
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <Image
                      src={relatedBlog.image}
                      alt={relatedBlog.title}
                      width={400}
                      height={250}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    {/* Meta Info */}
                    <div className="flex items-center justify-between gap-4 text-sm text-[#D4AF37] mb-3">
                      <span className="flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {relatedBlog.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {relatedBlog.readTime}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                      {relatedBlog.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-[#BCBCBC] text-sm mb-4 line-clamp-2 flex-grow">
                      {relatedBlog.excerpt}
                    </p>

                    {/* Read More Button */}
                    <div className="flex items-center text-[#D4AF37] font-semibold text-sm group-hover:gap-2 transition-all">
                      Read Full Article
                      <span className="group-hover/btn:translate-x-3 transition-transform ml-2">
                        →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <ReservationForm />
    </section>
  );
}
